import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Shuffle, 
  ArrowLeft, 
  Loader2, 
  UserPlus,
  Phone,
  User,
  AtSign,
  CheckCircle,
  Search,
  Users,
  Gift,
  Shield,
  Store,
  Link2,
  QrCode,
  Copy,
  Download,
  ExternalLink,
  Info,
  Smartphone
} from "lucide-react";
import { toast } from "sonner";
import { drawsAPI, membersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";

export default function ShopMode() {
  const { user } = useAuth();
  const phoneInputRef = useRef(null);
  
  const [draws, setDraws] = useState([]);
  const [selectedDrawId, setSelectedDrawId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastRegistered, setLastRegistered] = useState(null);
  const [existingMember, setExistingMember] = useState(null);

  const [formData, setFormData] = useState({
    pseudo: "",
    first_name: "",
    phone: "",
    email: ""
  });

  useEffect(() => {
    loadDraws();
  }, []);

  const loadDraws = async () => {
    try {
      const res = await drawsAPI.list();
      // Only show non-completed draws
      const activeDraws = res.data.filter(d => d.status !== "completed");
      setDraws(activeDraws);
      if (activeDraws.length > 0) {
        setSelectedDrawId(activeDraws[0].id);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des tirages");
    } finally {
      setLoading(false);
    }
  };

  // Search member when phone is entered
  const handlePhoneChange = async (phone) => {
    setFormData({...formData, phone});
    setExistingMember(null);
    
    // Clean phone number
    const cleanPhone = phone.replace(/\s/g, "");
    
    if (cleanPhone.length >= 10) {
      setSearching(true);
      try {
        const res = await membersAPI.searchByPhone(cleanPhone);
        if (res.data.found) {
          setExistingMember(res.data.member);
          setFormData({
            ...formData,
            phone: cleanPhone,
            pseudo: res.data.member.pseudo,
            first_name: res.data.member.first_name,
            email: res.data.member.email || ""
          });
          toast.info(`Client reconnu: ${res.data.member.first_name} (${res.data.member.pseudo})`);
        }
      } catch (error) {
        console.error("Search error", error);
      } finally {
        setSearching(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDrawId) {
      toast.error("Sélectionnez un tirage");
      return;
    }
    
    if (!formData.phone || formData.phone.length < 10) {
      toast.error("Numéro de téléphone invalide");
      return;
    }
    
    if (!formData.pseudo || !formData.first_name) {
      toast.error("Pseudo et prénom requis");
      return;
    }

    setSubmitting(true);
    try {
      if (existingMember) {
        // Add existing member to draw
        await membersAPI.addToDraw(existingMember.id, selectedDrawId);
      } else {
        // Quick register new member
        await membersAPI.quickRegister({
          ...formData,
          draw_id: selectedDrawId
        });
      }
      
      setLastRegistered({
        name: `${formData.first_name} (${formData.pseudo})`,
        phone: formData.phone,
        drawTitle: draws.find(d => d.id === selectedDrawId)?.title
      });
      setShowSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ pseudo: "", first_name: "", phone: "", email: "" });
        setExistingMember(null);
        phoneInputRef.current?.focus();
      }, 3000);
      
      toast.success("Participant inscrit avec succès !");
      loadDraws(); // Refresh draw counts
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDraw = draws.find(d => d.id === selectedDrawId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-white hover:bg-slate-700">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                  Mode Boutique
                </h1>
                <p className="text-sm text-slate-400">{user?.company_name}</p>
              </div>
            </div>
          </div>
          
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <span className="w-2 h-2 rounded-full bg-green-400 mr-2 animate-pulse"></span>
            En ligne
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Success Animation */}
        <AnimatePresence>
          {showSuccess && lastRegistered && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm"
            >
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 border-none text-white max-w-md mx-4">
                <CardContent className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", damping: 10, stiffness: 200, delay: 0.2 }}
                  >
                    <CheckCircle className="w-20 h-20 mx-auto mb-4" />
                  </motion.div>
                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
                    Inscription réussie !
                  </h2>
                  <p className="text-white/80 mb-4">
                    <strong>{lastRegistered.name}</strong> participe maintenant à
                  </p>
                  <Badge className="bg-white/20 text-white border-white/30 text-base px-4 py-2">
                    {lastRegistered.drawTitle}
                  </Badge>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {draws.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <Shuffle className="w-16 h-16 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Aucun tirage actif</h3>
              <p className="text-slate-400 mb-6">Créez un tirage pour utiliser le mode boutique</p>
              <Link to="/draws/new">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Créer un tirage
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <UserPlus className="w-5 h-5 text-amber-400" />
                    Inscrire un client
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Entrez le numéro de téléphone pour vérifier si le client existe déjà
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Draw Selection */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Tirage au sort</Label>
                      <Select value={selectedDrawId} onValueChange={setSelectedDrawId}>
                        <SelectTrigger className="h-12 bg-slate-700/50 border-slate-600 text-white">
                          <SelectValue placeholder="Sélectionner un tirage" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {draws.map((draw) => (
                            <SelectItem 
                              key={draw.id} 
                              value={draw.id}
                              className="text-white focus:bg-slate-700"
                            >
                              <div className="flex items-center gap-2">
                                <span>{draw.title}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {draw.participants?.length || 0} participants
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Phone - Primary Field */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Numéro de téléphone *</Label>
                      <div className="relative">
                        <Phone className="w-5 h-5 absolute left-3 top-3.5 text-slate-400" />
                        <Input
                          ref={phoneInputRef}
                          type="tel"
                          placeholder="06 12 34 56 78"
                          value={formData.phone}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 pl-11 text-lg"
                          data-testid="shop-phone-input"
                          autoFocus
                        />
                        {searching && (
                          <Loader2 className="w-5 h-5 absolute right-3 top-3.5 text-slate-400 animate-spin" />
                        )}
                        {existingMember && (
                          <CheckCircle className="w-5 h-5 absolute right-3 top-3.5 text-green-400" />
                        )}
                      </div>
                      {existingMember && (
                        <p className="text-sm text-green-400 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Client reconnu - {existingMember.draws_participated?.length || 0} tirage(s) précédent(s)
                        </p>
                      )}
                    </div>

                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Prénom *</Label>
                        <div className="relative">
                          <User className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                          <Input
                            placeholder="Marie"
                            value={formData.first_name}
                            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                            className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 pl-11"
                            data-testid="shop-firstname-input"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-slate-300">Pseudo *</Label>
                        <div className="relative">
                          <AtSign className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                          <Input
                            placeholder="marie_chance"
                            value={formData.pseudo}
                            onChange={(e) => setFormData({...formData, pseudo: e.target.value})}
                            className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 pl-11"
                            data-testid="shop-pseudo-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Email (optional) */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">
                        Email <span className="text-slate-500">(optionnel - pour RGPD)</span>
                      </Label>
                      <Input
                        type="email"
                        placeholder="marie@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                      />
                      <p className="text-xs text-slate-500">
                        Un email sera envoyé avec un lien pour supprimer ses données (RGPD)
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting || !selectedDrawId}
                      className="w-full h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold text-lg"
                      data-testid="shop-submit-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : existingMember ? (
                        <>
                          <UserPlus className="w-6 h-6 mr-2" />
                          Ajouter au tirage
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-6 h-6 mr-2" />
                          Inscrire le participant
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Side Info */}
            <div className="space-y-6">
              {/* Mode Boutique Explanation */}
              <Card className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 border-amber-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-amber-400 text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Info className="w-4 h-4" />
                    Qu'est-ce que le Mode Boutique ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Le <strong className="text-amber-400">Mode Boutique</strong> vous permet d'inscrire rapidement vos clients en magasin à vos tirages au sort. 
                    Idéal pour les points de vente physiques !
                  </p>
                  <ul className="text-xs text-slate-400 space-y-1.5 list-none">
                    <li className="flex items-start gap-2">
                      <Smartphone className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                      <span>Utilisez cette page sur tablette ou ordinateur en caisse</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <QrCode className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                      <span>Affichez le QR code pour inscription autonome par le client</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <UserPlus className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                      <span>Reconnaissance automatique des clients fidèles</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* QR Code & Link Generator */}
              {selectedDraw && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <QrCode className="w-4 h-4 text-primary" />
                      Lien de participation
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Partagez ce lien ou QR code pour que vos clients s'inscrivent eux-mêmes
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* QR Code */}
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <QRCodeSVG 
                        value={`${window.location.origin}/draw/${selectedDraw.id}/register`}
                        size={140}
                        level="M"
                        includeMargin={false}
                      />
                    </div>
                    
                    {/* Link display */}
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-xs">Lien direct</Label>
                      <div className="flex gap-2">
                        <Input
                          readOnly
                          value={`${window.location.origin}/draw/${selectedDraw.id}/register`}
                          className="text-xs bg-slate-700/50 border-slate-600 text-slate-300 h-9"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 border-slate-600 hover:bg-slate-700 text-slate-300"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/draw/${selectedDraw.id}/register`);
                            toast.success("Lien copié !");
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-slate-600 hover:bg-slate-700 text-slate-300 text-xs"
                        onClick={() => window.open(`/draw/${selectedDraw.id}/register`, '_blank')}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Ouvrir la page
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-slate-600 hover:bg-slate-700 text-slate-300 text-xs"
                        onClick={() => {
                          const svg = document.querySelector('.qr-download-target svg');
                          if (!svg) {
                            // Fallback: create a temporary canvas
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            const svgElement = document.querySelector('svg');
                            if (svgElement) {
                              const svgData = new XMLSerializer().serializeToString(svgElement);
                              const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                              const url = URL.createObjectURL(svgBlob);
                              const link = document.createElement('a');
                              link.download = `qr-${selectedDraw.title.replace(/\s+/g, '-')}.svg`;
                              link.href = url;
                              link.click();
                              URL.revokeObjectURL(url);
                              toast.success("QR code téléchargé !");
                            }
                          }
                        }}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        Télécharger
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Selected Draw Info */}
              {selectedDraw && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base" style={{ fontFamily: 'Outfit' }}>
                      Tirage en cours
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{selectedDraw.title}</h3>
                      <p className="text-sm text-slate-400">{selectedDraw.organizer_name}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4 text-blue-400" />
                        {selectedDraw.participants?.length || 0} participants
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Gift className="w-4 h-4 text-amber-400" />
                        {selectedDraw.prizes?.length || 0} lots
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* RGPD Info */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-white mb-1">Conformité RGPD</h4>
                      <p className="text-xs text-slate-400">
                        Les participants peuvent demander la suppression de leurs données à tout moment via le lien envoyé par email.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Tips */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Conseil
                  </h4>
                  <p className="text-xs text-slate-300">
                    Le numéro de téléphone sert d'identifiant unique. Un même client peut participer à plusieurs tirages avec le même numéro.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
